import React, { useCallback, useEffect, useRef, useState } from "react";
import { growFrameWhenRecaptchaClicked, stopRecaptchaGrow, VERSAPAY_CLIENT_URL } from "../../../utils/versapayFrame";
import { Golfer } from "../../../types/types";
import {
  getProducts,
  SearchMembers,
  checkEmailExists,
  createOrUpdateMember,
  ApplyPromoCode,
  UpdatePromoCode,
  createVersapaySession,
  confirmVersapayPayment,
  getAdminOwners,
  logAuditEvent,
} from "../../../api/api-hook";
import { getAdminSession } from "../../../utils/adminAuth";
import {
  normalizeInput,
  formatDOB,
  convertFromISOToUS,
  isValidDOB,
  isUnder25,
  usaStates,
  usDateToTimestamp,
} from "../../../utils/utils";

// ── Types ────────────────────────────────────────────────────────────────────

interface VipProduct {
  id: string;
  name: string;
  label: string;
  description?: string;
  price?: number;
  product_category: string;
  tabKey: string;
  imageUrl: string;
  under_25: string;
  product_id: string;
  bagTagPrice: number;
}

type Owner = { id: string; firstName: string; lastName: string; email: string };

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;

// ── VersaPay SDK plumbing (module-local, isolated from public Step6) ─────────

declare global {
  interface Window {
    versapay?: {
      initClient: (
        sessionId: string,
        styles?: Record<string, any>,
        fontUrls?: string[]
      ) => Promise<{
        initFrame: (el: HTMLElement, w: string, h: string) => Promise<void>;
        submitEvents: () => Promise<void>;
        onApproval?: (
          onSuccess: (result: any) => void,
          onError: (error: any) => void
        ) => void;
      }>;
    };
  }
}

const VP_SCRIPT_ID = "versapay-sdk-script-admin";
const VP_SCRIPT_SRC = VERSAPAY_CLIENT_URL;

// All admin VP pages share the same script ID and window-level listener tracking so
// loading a new page's SDK always removes the previous page's script and listeners.
function startAdminVPTracking() {
  if ((window as any).__adminVpTrackingActive) return;
  (window as any).__adminVpTrackingActive = true;
  (window as any).__adminVpListeners = [];
  const _orig = window.addEventListener.bind(window);
  (window as any).addEventListener = function (type: string, listener: any, options?: any) {
    if (type === "message") ((window as any).__adminVpListeners as any[]).push([listener, options]);
    return _orig(type, listener, options);
  };
}

function removeAdminVPListeners() {
  const listeners: Array<[any, any]> = (window as any).__adminVpListeners || [];
  listeners.forEach(([l, opts]) =>
    window.removeEventListener("message", l as EventListener, opts as any)
  );
  (window as any).__adminVpListeners = [];
}

// Synchronously destroy any existing VP SDK instance — call this before rendering
// the payment step so VP's old SDK can't react to the new container appearing in DOM.
function killExistingAdminVP() {
  removeAdminVPListeners();
  document.getElementById(VP_SCRIPT_ID)?.remove();
  // Remove any VP iframes VP may have appended to document.body (outside our container)
  document.querySelectorAll('iframe[src*="versapay"], iframe[src*="ecommerce-api"]').forEach((el) => el.remove());
  try { (window as any).versapay = undefined; } catch (_) {}
}

async function loadAdminVPScript(): Promise<void> {
  startAdminVPTracking();
  killExistingAdminVP();
  await new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.id = VP_SCRIPT_ID;
    s.src = `${VP_SCRIPT_SRC}?_t=${Date.now()}`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load VersaPay SDK."));
    document.body.appendChild(s);
  });
}

// ── Brand ────────────────────────────────────────────────────────────────────

const BRAND_GREEN = "#023822";

// ── Step breadcrumb ──────────────────────────────────────────────────────────

const STEP_LABELS: Record<Step, string> = {
  1: "Package",
  2: "Member",
  3: "Personal Info",
  4: "Address",
  5: "Add-ons",
  6: "Payment",
  7: "Done",
};

function StepBreadcrumb({ current }: { current: Step }) {
  const steps: Step[] = [1, 2, 3, 4, 5, 6, 7];
  return (
    <div className="d-flex align-items-center gap-1 mb-4 flex-wrap" style={{ fontSize: "0.8rem" }}>
      {steps.map((s) => (
        <React.Fragment key={s}>
          <span
            style={{
              padding: "0.2rem 0.6rem",
              borderRadius: 4,
              backgroundColor: s === current ? BRAND_GREEN : s < current ? "#c3e6cb" : "#e9ecef",
              color: s === current ? "#fff" : s < current ? "#155724" : "#6c757d",
              fontWeight: s === current ? 600 : 400,
            }}
          >
            {STEP_LABELS[s]}
          </span>
          {s < 7 && <span style={{ color: "#adb5bd" }}>›</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="bg-light border px-3 py-2 mb-4 text-uppercase fw-semibold"
      style={{ fontSize: "0.9rem" }}
    >
      {children}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const EMPTY_GOLFER: Golfer = {
  firstName: "", lastName: "", email: "", phone: "", sex: "",
  date_of_birth: "", address: "", street_address_2: "", city: "",
  state: "VA", zip: "", bagTag: false, donation: false,
  personalization: "", donation_amount: 0, vip_card_type: "",
  vip_package: "", memberNumber: "", cart_total: 0, quantity: "1",
  sameAsBilling: true, ShippingAddress: "", ShippingStreet_address_2: "",
  ShippingCity: "", ShippingState: "VA", ShippingZip: "",
  product_id: "", GiftPurchase: false, GiftProductId: "", GiftVipPackage: "",
  ghinNumber: "", bagTagPrice: 0, promo_value: 0, promo_amount_applied: 0,
  promo_object_id: 0, promo_code: "", ownerId: "",
};

const PurchaseMembership = () => {
  // ── wizard state ──────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>(1);
  const [fd, setFd] = useState<Golfer>({ ...EMPTY_GOLFER });

  const update = <K extends keyof Golfer>(field: K, value: Golfer[K]) =>
    setFd((prev) => ({ ...prev, [field]: value }));

  // ── step 1: products + owners ─────────────────────────────────────────────
  const [products, setProducts] = useState<VipProduct[]>([]);
  const [activeTab, setActiveTab] = useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const [owners, setOwners] = useState<Owner[]>([]);
  const sessionOwnerId = getAdminSession()?.ownerId ?? null;

  useEffect(() => {
    // Pre-set owner from session if available
    if (sessionOwnerId) update("ownerId", sessionOwnerId as any);

    setProductsLoading(true);
    getProducts({ property: "product_category", value: "vip" })
      .then((raw) => {
        const data: VipProduct[] = typeof raw === "string" ? JSON.parse(raw) : raw;
        setProducts(data);
        if (data.length > 0) {
          setActiveTab(data[0].tabKey);
          update("vip_package", data[0].tabKey);
          update("product_id", data[0].product_id as any);
        }
      })
      .finally(() => setProductsLoading(false));

    getAdminOwners().then((r) => {
      if (r.success) setOwners(r.owners);
    });
  }, []);

  const handleTabSelect = (tabKey: string) => {
    const p = products.find((p) => p.tabKey === tabKey);
    setActiveTab(tabKey);
    update("vip_package", tabKey);
    update("vip_card_type", p?.under_25 ? "Digital" : "");
    if (p) update("product_id", p.product_id as any);
  };

  const descriptionToList = (description: string) => {
    if (!description) return null;
    const blocks = description.split(/\r?\n\r?\n+/);
    return (
      <div className="mb-3">
        {blocks.map((block, i) => {
          const lines = block.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
          if (lines.length === 1) return <div key={i} className="mb-2">{lines[0]}</div>;
          return <ul key={i} className="mb-3">{lines.map((l, j) => <li key={j}>{l}</li>)}</ul>;
        })}
      </div>
    );
  };

  // ── step 2: member search ─────────────────────────────────────────────────
  const [searchResults, setSearchResults] = useState<Golfer[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSearched(true);
    setSearchLoading(true);
    try {
      const raw = await SearchMembers(fd.lastName || "", fd.memberNumber || "");
      const data: Golfer[] = typeof raw === "string" ? JSON.parse(raw) : raw;
      setSearchResults(Array.isArray(data) ? data : []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectMember = (member: Golfer) => {
    setFd((prev) => ({
      ...prev,
      ...member,
      vip_package: prev.vip_package,
      vip_card_type: prev.vip_card_type,
      product_id: prev.product_id,
      ownerId: prev.ownerId,
      // DOB: convert from ISO if needed
      date_of_birth: member.date_of_birth
        ? member.date_of_birth.includes("-")
          ? convertFromISOToUS(member.date_of_birth)
          : member.date_of_birth
        : "",
      phone: normalizeInput(member.phone),
    }));
    setStep(3);
  };

  const handleNewMember = () => {
    setFd((prev) => ({
      ...EMPTY_GOLFER,
      vip_package: prev.vip_package,
      vip_card_type: prev.vip_card_type,
      product_id: prev.product_id,
      ownerId: prev.ownerId,
    }));
    setStep(3);
  };

  // ── step 3: personal info ─────────────────────────────────────────────────
  const [emailStatus, setEmailStatus] = useState<"idle" | "checking" | "exists" | "error">("idle");
  const [emailMessage, setEmailMessage] = useState("");
  const [dobError, setDobError] = useState("");

  const handleEmailBlur = async () => {
    const email = fd.email || "";
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailStatus("idle"); setEmailMessage(""); return;
    }
    setEmailStatus("checking");
    try {
      const raw = await checkEmailExists(email);
      const data = JSON.parse(raw || "{}");
      if (data.exists && data.contact?.id != fd.id) {
        setEmailStatus("error");
        setEmailMessage("That email is already taken.");
      } else {
        setEmailStatus("idle"); setEmailMessage("");
      }
    } catch {
      setEmailStatus("idle"); setEmailMessage("");
    }
  };

  const handleDOBChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const formatted = formatDOB(e.currentTarget.value);
    update("date_of_birth", formatted);
    // Allow empty — DOB is optional
    if (!formatted) { setDobError(""); return; }
    if (formatted.length !== 10) { setDobError("Enter a valid date (MM/DD/YYYY)"); return; }
    if (!isValidDOB(formatted)) { setDobError("Enter a valid date (MM/DD/YYYY)"); return; }
    if ((fd.vip_package || "").toLowerCase() === "bronze-18-25" && !isUnder25(formatted)) {
      setDobError("This package is only for golfers under 25."); return;
    }
    setDobError("");
  };

  // ── step 5: add-on products ───────────────────────────────────────────────
  const [addOnProducts, setAddOnProducts] = useState<VipProduct[]>([]);
  const [addOnsLoading, setAddOnsLoading] = useState(false);

  // Load add-ons when we enter step 5
  useEffect(() => {
    if (step !== 5) return;
    setAddOnsLoading(true);
    getProducts({ property: "product_category", value: "addon" })
      .then((raw) => {
        const data: VipProduct[] = typeof raw === "string" ? JSON.parse(raw) : raw;
        setAddOnProducts(data);
        const bagTag = data.find((p) =>
          (p.tabKey || p.name || p.label || "").toLowerCase().includes("bagtag")
        );
        if (bagTag?.price != null) update("bagTagPrice", Number(bagTag.price) as any);
      })
      .finally(() => setAddOnsLoading(false));
  }, [step]);

  // ── step 6: VIP product for pricing ──────────────────────────────────────
  const [vipProduct, setVipProduct] = useState<VipProduct | null>(null);

  useEffect(() => {
    if (step !== 6) return;
    getProducts({ property: "tabKey", value: fd.vip_package || "" }).then((raw) => {
      const data: VipProduct[] = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (data.length > 0) setVipProduct(data[0]);
    });
  }, [step]);

  // ── Derived totals ────────────────────────────────────────────────────────
  const unitPrice = Number(vipProduct?.price) || 0;
  const bagTagTotal = fd.bagTag ? Number(fd.bagTagPrice) || 0 : 0;
  const donationTotal = fd.donation ? Number(fd.donation_amount) || 0 : 0;
  const cartTotal = unitPrice + bagTagTotal + donationTotal;
  const discount = Number(fd.promo_amount_applied || 0);
  const finalTotal = Math.max(0, cartTotal - discount);
  const finalTotalCents = Math.round(finalTotal * 100);

  // ── Promo code ────────────────────────────────────────────────────────────
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  const applyPromoCode = async () => {
    if (isApplyingPromo) return;
    const code = promoInput.trim();
    if (!code) { setPromoError("Enter a promo code."); return; }
    const selectedSku = vipProduct?.product_id || fd.product_id || "";
    if (!selectedSku) { setPromoError("Please select a package first."); return; }
    try {
      setIsApplyingPromo(true);
      setPromoError(null);
      const data: any = await ApplyPromoCode(code, cartTotal, Number(selectedSku), unitPrice);
      if (!data?.success) {
        const errCode = data?.error;
        const pv = Number(data?.promo_value || 0);
        if (errCode === "not_found") setPromoError("Promo code not found.");
        else if (errCode === "not_active") setPromoError("This promo code is already used or reserved.");
        else if (errCode === "cart_too_low") setPromoError(pv ? `Promo worth $${pv.toFixed(2)} requires a $${pv.toFixed(2)}+ order.` : "Cart total too low.");
        else setPromoError("Could not apply that promo code.");
        setPromoApplied(false); setAppliedPromoCode(null);
        return;
      }
      const promoValue = Number(data.amountOff ?? 0);
      if (!Number.isFinite(promoValue) || promoValue <= 0) {
        setPromoError("That promo code is invalid."); return;
      }
      update("promo_code", code as any);
      update("promo_value", promoValue as any);
      update("promo_amount_applied", promoValue as any);
      if (data.promo_object_id) update("promo_object_id", String(data.promo_object_id) as any);
      setPromoApplied(true);
      setAppliedPromoCode(code.toUpperCase());
      setPromoInput("");
      setPromoError(null);
      // Reset session for new total
      clearVPSession();
      setVpRetryNonce((n) => n + 1);
    } catch {
      setPromoError("Could not apply promo code. Please try again.");
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const removePromo = async () => {
    const codeToRelease = appliedPromoCode;
    if (!codeToRelease) return;
    try { await UpdatePromoCode(codeToRelease, "Active"); } catch (_) {}
    setPromoApplied(false); setAppliedPromoCode(null); setPromoError(null);
    update("promo_code", "" as any);
    update("promo_value", 0 as any);
    update("promo_amount_applied", 0 as any);
    clearVPSession();
    setVpRetryNonce((n) => n + 1);
  };

  // ── VersaPay ──────────────────────────────────────────────────────────────
  const containerRef = useRef<HTMLDivElement | null>(null);
  const clientRef = useRef<any>(null);
  const sessionIdRef = useRef("");
  const sessionAmountRef = useRef(0);
  const setupInFlightRef = useRef(false);
  const frameMountedRef = useRef(false);
  const mountedSessionRef = useRef("");
  const callbackBoundSessionRef = useRef("");
  const mountedRef = useRef(true);
  const processingApprovalRef = useRef(false);
  const approvalReceivedRef = useRef(false);
  const handleApprovalRef = useRef<((result: any, sessionId: string) => Promise<void>) | null>(null);
  const clearVPSessionRef = useRef<() => void>(() => {});
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fdRef = useRef(fd);
  fdRef.current = fd;
  const finalTotalRef = useRef(finalTotal);
  finalTotalRef.current = finalTotal;
  const finalTotalCentsRef = useRef(finalTotalCents);
  finalTotalCentsRef.current = finalTotalCents;
  const vipUnitPriceCentsRef = useRef(0);
  vipUnitPriceCentsRef.current = Math.round(unitPrice * 100);

  const [vpPageLoading, setVpPageLoading] = useState(false);
  const [vpLoading, setVpLoading] = useState(false);
  const [vpError, setVpError] = useState<string | null>(null);
  const [vpRetryNonce, setVpRetryNonce] = useState(0);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const sameAsBillingRef = useRef(true);
  sameAsBillingRef.current = sameAsBilling;
  const [orderComplete, setOrderComplete] = useState(false);
  const [completedGolfer, setCompletedGolfer] = useState<Golfer | null>(null);
  const [assignedVipCardNumber, setAssignedVipCardNumber] = useState<string>("");
  const [payError, setPayError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"credit_card" | "check" | "cash" | "misc">("credit_card");
  const [checkNumber, setCheckNumber] = useState("");
  const [miscNote,    setMiscNote]    = useState("");

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const clearVPSession = useCallback(() => {
    if (containerRef.current) containerRef.current.innerHTML = "";
    clientRef.current = null;
    frameMountedRef.current = false;
    callbackBoundSessionRef.current = "";
    mountedSessionRef.current = "";
    sessionIdRef.current = "";
    sessionAmountRef.current = 0;
  }, []);

  // handleApproval — runs after card is approved by VersaPay
  const handleApproval = useCallback(async (result: any, sessionToUse: string) => {
    approvalReceivedRef.current = true;
    if (processingApprovalRef.current) return;
    try {
      processingApprovalRef.current = true;
      setProcessingPayment(true);
      setVpLoading(true);
      setVpError(null);

      const currentFd = fdRef.current;
      const currentFinalTotal = finalTotalRef.current;
      const currentFinalTotalCents = finalTotalCentsRef.current;

      const lineItems: Array<{ type: string; number: string; description: string; price: number; quantity: number; discount: number; }> = [];

      const vipCents = vipUnitPriceCentsRef.current;
      if (vipCents > 0) {
        lineItems.push({ type: "Item", number: currentFd.product_id || "VIP", description: currentFd.vip_package || "", price: vipCents, quantity: 1, discount: 0 });
      }
      const bagTagCents = Math.round(Number(currentFd.bagTagPrice || 0) * 100);
      if (currentFd.bagTag && bagTagCents > 0) {
        lineItems.push({ type: "Item", number: "BAG-TAG", description: currentFd.personalization ? `Bag Tag — ${currentFd.personalization}` : "Bag Tag", price: bagTagCents, quantity: 1, discount: 0 });
      }
      const donCents = Math.round(Number(currentFd.donation_amount || 0) * 100);
      if (currentFd.donation && donCents > 0) {
        lineItems.push({ type: "Item", number: "DONATION", description: "Donation", price: donCents, quantity: 1, discount: 0 });
      }
      const discountCents = Math.round(Number(currentFd.promo_amount_applied || 0) * 100);
      if (discountCents > 0) {
        lineItems.push({ type: "Item", number: "PROMO", description: `Promo: ${currentFd.promo_code || ""}`, price: -discountCents, quantity: 1, discount: 0 });
      }
      if (lineItems.length === 0) {
        lineItems.push({ type: "Item", number: "VIP", description: "VIP Golf Card", price: currentFinalTotalCents, quantity: 1, discount: 0 });
      }

      const useSameAsBilling = sameAsBillingRef.current;
      const billingAddress1 = useSameAsBilling ? currentFd.address       : (currentFd.ShippingAddress  || currentFd.address);
      const billingCity     = useSameAsBilling ? currentFd.city           : (currentFd.ShippingCity     || currentFd.city);
      const billingState    = useSameAsBilling ? currentFd.state          : (currentFd.ShippingState    || currentFd.state);
      const billingZip      = useSameAsBilling ? currentFd.zip            : (currentFd.ShippingZip      || currentFd.zip);

      const confirmResponse = await confirmVersapayPayment({
        sessionId: sessionToUse,
        approvalResult: result,
        amount: currentFinalTotalCents,
        firstName: currentFd.firstName,
        lastName: currentFd.lastName,
        email: currentFd.email,
        address1: billingAddress1,
        city: billingCity,
        state: billingState,
        zip: billingZip,
        vipCardNumber: currentFd.ghinNumber || "",
        lineItems,
      } as any);

      if (!confirmResponse?.success) throw new Error(confirmResponse?.error || "Failed to confirm payment.");

      const updatedGolfer: Golfer = {
        ...currentFd,
        cart_total: currentFinalTotal,
        sameAsBilling: true,
        GiftPurchase: false,
        versapaySessionId: sessionToUse as any,
        versapayApprovalResult: result as any,
        versapayTransactionId: confirmResponse.transactionId as any,
        versapayOrderNumber: (confirmResponse.orderNumber ?? "") as any,
        versapayVpOrderNumber: (confirmResponse.vpOrderNumber ?? "") as any,
        ccToken: (confirmResponse.ccToken ?? "") as any,
        ccLastFour: (confirmResponse.ccLastFour ?? "") as any,
        ccType: (confirmResponse.ccType ?? "") as any,
      };

      const hubspotPayload = {
        ...updatedGolfer,
        payment_method: "Credit Card",
        date_of_birth: updatedGolfer.date_of_birth
          ? usDateToTimestamp(updatedGolfer.date_of_birth)
          : undefined,
        created_source: "Admin",
};

      const upsertResp = await createOrUpdateMember(hubspotPayload as any);
      const upsertData = typeof upsertResp === "string" ? JSON.parse(upsertResp) : upsertResp;
      setAssignedVipCardNumber(upsertData?.vipCardNumber || "");
      setCompletedGolfer(updatedGolfer);
      setOrderComplete(true);
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "purchase",
        label:   "Membership Purchased",
        details: `${updatedGolfer.firstName || ""} ${updatedGolfer.lastName || ""} — ${updatedGolfer.vip_package || "VIP"} package (Credit Card)`.trim(),
      });
      setStep(7);
    } catch (err: any) {
      const status = err?.response?.status;
      const bodyText: string = err?.response?.data ?? err?.message ?? "";
      const is504 = status === 504 || (typeof bodyText === "string" && bodyText.includes("504"));
      setPayError(is504
        ? "The request timed out. Your card has not been charged — please wait and try again."
        : err?.response?.data?.error || err?.response?.data?.message || err?.message || "Payment approved but processing failed.");
    } finally {
      processingApprovalRef.current = false;
      setProcessingPayment(false);
      setVpLoading(false);
    }
  }, []);

  handleApprovalRef.current = handleApproval;
  clearVPSessionRef.current = clearVPSession;

  // VersaPay setup effect — only runs when on step 6
  useEffect(() => {
    if (step !== 6) return;

    const setup = async () => {
      if (paymentMethod !== "credit_card") {
        clearVPSession();
        setVpPageLoading(false);
        return;
      }
      if (!containerRef.current || !vipProduct) { setVpPageLoading(true); return; }
      if (!finalTotalCents || finalTotalCents <= 0) { setVpPageLoading(false); return; }

      if (frameMountedRef.current && sessionIdRef.current && mountedSessionRef.current === sessionIdRef.current && sessionAmountRef.current === finalTotalCents) {
        setVpPageLoading(false); return;
      }

      if (setupInFlightRef.current) return;
      setupInFlightRef.current = true;
      setVpPageLoading(true);
      setVpError(null);

      try {
        // Always reload SDK fresh — VP SDK retains internal state across initClient calls
        await loadAdminVPScript();
        sessionIdRef.current = ""; sessionAmountRef.current = 0;

        if (!mountedRef.current || !containerRef.current) return;
        if (!window.versapay) throw new Error("VersaPay SDK did not load.");

        // Reuse existing session only if the SDK was NOT reloaded and amount matches
        const sessResp = await createVersapaySession({
          amount: finalTotalCents,
          firstName: fdRef.current.firstName,
          lastName: fdRef.current.lastName,
          email: fdRef.current.email,
        });
        if (!sessResp?.sessionId) throw new Error("No VersaPay session ID returned.");
        const sessionToUse = sessResp.sessionId;
        sessionIdRef.current = sessionToUse;
        sessionAmountRef.current = finalTotalCents;

        if (!mountedRef.current || !containerRef.current) return;

        const client = await window.versapay.initClient(sessionToUse);
        clientRef.current = client;

        if (!mountedRef.current || !containerRef.current) return;

        if (client.onApproval) {
          const boundSession = sessionToUse;
          client.onApproval(
            (result: any) => {
              stopRecaptchaGrow();
              handleApprovalRef.current?.(result, boundSession);
            },
            (err: any) => {
              stopRecaptchaGrow();
              if (safetyTimeoutRef.current) { clearTimeout(safetyTimeoutRef.current); safetyTimeoutRef.current = null; }
              const msg = typeof err === "string" ? err : err?.message || err?.error || "Payment not approved.";
              setVpError(msg);
              setProcessingPayment(false);
              setVpLoading(false);
              clearVPSessionRef.current?.();
            }
          );
          callbackBoundSessionRef.current = sessionToUse;
        }

        if (!frameMountedRef.current || mountedSessionRef.current !== sessionToUse) {
          containerRef.current.innerHTML = "";
          await client.initFrame(containerRef.current, "600px", "720px");
          if (!mountedRef.current || !containerRef.current) return;
          const vpIframe = containerRef.current.querySelector("iframe");
          if (vpIframe) { vpIframe.style.width = "100%"; vpIframe.style.maxWidth = "100%"; vpIframe.style.height = "270px"; }
          frameMountedRef.current = true;
          mountedSessionRef.current = sessionToUse;
        }
      } catch (err: any) {
        if (mountedRef.current) {
          setVpError(err?.response?.data?.error || err?.message || "Failed to load payment form.");
          frameMountedRef.current = false;
          mountedSessionRef.current = "";
        }
      } finally {
        setupInFlightRef.current = false;
        if (mountedRef.current) setVpPageLoading(false);
      }
    };

    setup();
  }, [step, vipProduct, finalTotalCents, vpRetryNonce, paymentMethod]);

  const handlePay = async () => {
    try {
      setVpError(null);
      if (vpLoading || processingPayment || vpPageLoading) return;
      if (!clientRef.current) throw new Error("Payment form not ready.");
      approvalReceivedRef.current = false;
      setProcessingPayment(true);
      (window as any).__adminVpSessionWasUsed = true;
      growFrameWhenRecaptchaClicked(containerRef.current?.querySelector("iframe"));
      await clientRef.current.submitEvents();
      if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = setTimeout(() => {
        safetyTimeoutRef.current = null;
        if (!approvalReceivedRef.current && !processingApprovalRef.current) {
          setVpError("Payment session expired. Please re-enter your card details and try again.");
          setProcessingPayment(false);
          clearVPSessionRef.current?.();
        }
      }, 60000);
    } catch (err: any) {
      stopRecaptchaGrow();
      const vpIframe = containerRef.current?.querySelector("iframe");
      if (vpIframe) vpIframe.style.height = "270px";
      setVpError(err?.response?.data?.message || err?.message || "Could not submit payment.");
      setProcessingPayment(false);
    }
  };

  const handleCancelPayment = () => {
    setProcessingPayment(false);
    setVpLoading(false);
    setVpError(null);
    setPayError(null);
    processingApprovalRef.current = false;
    approvalReceivedRef.current = false;
    clearVPSession();
    setVpRetryNonce((n) => n + 1);
  };

  const handleFreeOrder = async () => {
    if (vpLoading || processingPayment) return;
    setProcessingPayment(true); setVpLoading(true); setVpError(null);
    try {
      const hubspotPayload = {
        ...fdRef.current,
        cart_total: 0,
        date_of_birth: fdRef.current.date_of_birth ? usDateToTimestamp(fdRef.current.date_of_birth) : undefined,
        created_source: "Admin",
};
      const upsertResp = await createOrUpdateMember(hubspotPayload as any);
      const upsertData = typeof upsertResp === "string" ? JSON.parse(upsertResp) : upsertResp;
      setAssignedVipCardNumber(upsertData?.vipCardNumber || "");
      setCompletedGolfer(fdRef.current);
      setOrderComplete(true);
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "purchase",
        label:   "Membership Purchased",
        details: `${fdRef.current.firstName || ""} ${fdRef.current.lastName || ""} — ${fdRef.current.vip_package || "VIP"} package (Free / $0)`.trim(),
      });
      setStep(7);
    } catch (err: any) {
      setPayError(err?.response?.data?.error || err?.message || "Order processing failed.");
    } finally {
      setProcessingPayment(false); setVpLoading(false);
    }
  };

  const handleOfflinePayment = async () => {
    if (processingPayment) return;
    if (paymentMethod === "check" && !checkNumber.trim()) return;
    setProcessingPayment(true); setVpLoading(true); setVpError(null); setPayError(null);
    try {
      const currentFd = fdRef.current;
      const pmLabel = { credit_card: "Credit Card", check: "Check", cash: "Cash", misc: "Miscellaneous" }[paymentMethod];
      const hubspotPayload = {
        ...currentFd,
        cart_total: finalTotalRef.current,
        payment_method: pmLabel,
        ...(paymentMethod === "check" ? { check_number: checkNumber.trim() } : {}),
        ...(paymentMethod === "misc" && miscNote.trim() ? { misc_note: miscNote.trim() } : {}),
        date_of_birth: currentFd.date_of_birth ? usDateToTimestamp(currentFd.date_of_birth) : undefined,
        created_source: "Admin",
      };
      const upsertResp = await createOrUpdateMember(hubspotPayload as any);
      const upsertData = typeof upsertResp === "string" ? JSON.parse(upsertResp) : upsertResp;
      setAssignedVipCardNumber(upsertData?.vipCardNumber || "");
      setCompletedGolfer(currentFd);
      setOrderComplete(true);
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "purchase",
        label:   "Membership Purchased",
        details: `${currentFd.firstName || ""} ${currentFd.lastName || ""} — ${currentFd.vip_package || "VIP"} package (${pmLabel || "Offline"})`.trim(),
      });
      setStep(7);
    } catch (err: any) {
      setPayError(err?.response?.data?.error || err?.message || "Order processing failed.");
    } finally {
      setProcessingPayment(false); setVpLoading(false);
    }
  };

  // ── Reset wizard ──────────────────────────────────────────────────────────
  const handleStartOver = () => {
    clearVPSession();
    (window as any).__adminVpSessionWasUsed = false;
    setFd({ ...EMPTY_GOLFER });
    setStep(1);
    setOrderComplete(false);
    setCompletedGolfer(null);
    setAssignedVipCardNumber("");
    setPayError(null);
    setVpError(null);
    setSameAsBilling(true);
    setPromoApplied(false);
    setAppliedPromoCode(null);
    setPromoInput("");
    setPromoError(null);
    setVipProduct(null);
    setHasSearched(false);
    setSearchResults([]);
    setEmailStatus("idle");
    setEmailMessage("");
    setDobError("");
    setPaymentMethod("credit_card");
    setCheckNumber("");
    setMiscNote("");
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <h4 className="mb-1" style={{ color: BRAND_GREEN }}>Purchase Membership</h4>
      <p className="text-muted small mb-1">
        Process a membership purchase on behalf of a member and assign it to a VSGA owner.
      </p>
      {fd.ownerId && owners.length > 0 && (
        <p className="text-muted small mb-4">
          Owner: <strong>{owners.find((o) => o.id === fd.ownerId)?.firstName} {owners.find((o) => o.id === fd.ownerId)?.lastName}</strong>
        </p>
      )}

      <StepBreadcrumb current={step} />

      {/* ── STEP 1: Package Selection ── */}
      {step === 1 && (
        <div>
          <SectionHeader>Choose Package</SectionHeader>

          {/* Owner assignment */}
          <div className="mb-4" style={{ maxWidth: 360 }}>
            <label className="form-label fw-semibold">Assigned Owner</label>
            {sessionOwnerId && owners.length > 0 ? (
              <div className="form-control bg-light text-muted" style={{ cursor: "default" }}>
                {(() => { const o = owners.find((o) => o.id === sessionOwnerId); return o ? `${o.firstName} ${o.lastName}` : "Loading…"; })()}
              </div>
            ) : (
              <select
                className="form-select"
                value={fd.ownerId || ""}
                onChange={(e) => update("ownerId", e.target.value as any)}
              >
                <option value="">— No owner —</option>
                {owners.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.firstName} {o.lastName} {o.email ? `(${o.email})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          {productsLoading && (
            <div className="text-center my-4">
              <div className="spinner-border spinner-border-sm me-2" role="status" />
              Loading packages…
            </div>
          )}

          {!productsLoading && (
            <>
              {/* Package tabs */}
              <div className="d-flex gap-2 flex-wrap mb-4">
                {products.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="btn btn-sm"
                    style={{
                      backgroundColor: activeTab === p.tabKey ? BRAND_GREEN : "transparent",
                      color: activeTab === p.tabKey ? "#fff" : BRAND_GREEN,
                      border: `1px solid ${BRAND_GREEN}`,
                      fontWeight: activeTab === p.tabKey ? 600 : 400,
                    }}
                    onClick={() => handleTabSelect(p.tabKey)}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{
                    backgroundColor: activeTab === "wv-vip-card" ? BRAND_GREEN : "transparent",
                    color: activeTab === "wv-vip-card" ? "#fff" : BRAND_GREEN,
                    border: `1px solid ${BRAND_GREEN}`,
                    fontWeight: activeTab === "wv-vip-card" ? 600 : 400,
                  }}
                  onClick={() => {
                    setActiveTab("wv-vip-card");
                    update("vip_package", "wv-vip-card");
                    update("vip_card_type", "");
                  }}
                >
                  WV VIP Card
                </button>
              </div>

              {/* Active product details */}
              {products.map((p) =>
                activeTab !== p.tabKey ? null : (
                  <div key={p.tabKey} className="border rounded p-4 mb-4" style={{ maxWidth: 600 }}>
                    <h5 className="mb-1" style={{ color: BRAND_GREEN }}>
                      {p.name} — ${p.price?.toFixed(2)}
                    </h5>
                    {p.description && (
                      <div className="text-muted small mt-2">{descriptionToList(p.description)}</div>
                    )}

                    {p.under_25 && (
                      <div className="form-check mt-3">
                        <input type="checkbox" id="age-confirm" className="form-check-input" required />
                        <label className="form-check-label small" htmlFor="age-confirm">
                          I confirm this package is for someone age 18–25.
                        </label>
                      </div>
                    )}

                    <div className="mt-3" style={{ maxWidth: 280 }}>
                      <label className="form-label small fw-semibold">Card Format</label>
                      <select
                        className="form-select form-select-sm"
                        value={fd.vip_card_type || ""}
                        onChange={(e) => update("vip_card_type", e.target.value)}
                        required
                      >
                        <option value="">Select format…</option>
                        {!p.under_25 && <option value="Paper">{p.name} — Booklet</option>}
                        <option value="Digital">{p.name} — Digital</option>
                      </select>
                    </div>
                  </div>
                )
              )}

              {/* WV VIP Card panel */}
              {activeTab === "wv-vip-card" && (
                <div className="border rounded p-4 mb-4" style={{ maxWidth: 600 }}>
                  <h5 className="mb-1" style={{ color: BRAND_GREEN }}>WV VIP Card</h5>
                  <p className="text-muted small mt-2">
                    Access to golf at participating West Virginia courses.
                  </p>
                  <div className="mt-3" style={{ maxWidth: 280 }}>
                    <label className="form-label small fw-semibold">Card Format</label>
                    <select
                      className="form-select form-select-sm"
                      value={fd.vip_card_type === "WV VIP Card" ? "" : fd.vip_card_type || ""}
                      onChange={(e) => update("vip_card_type", e.target.value)}
                      required
                    >
                      <option value="">Select format…</option>
                      <option value="Paper">WV VIP Card — Booklet</option>
                      <option value="Digital">WV VIP Card — Digital</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  disabled={!fd.vip_card_type || fd.vip_card_type === "WV VIP Card"}
                  onClick={() => setStep(2)}
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── STEP 2: Member Lookup ── */}
      {step === 2 && (
        <div>
          <SectionHeader>Find Member</SectionHeader>

          <form className="row gy-3 mb-4" onSubmit={handleSearch}>
            <div className="col-md-4">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                className="form-control"
                value={fd.lastName || ""}
                onChange={(e) => update("lastName", e.target.value)}
                placeholder="Last name"
                required
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">VIP Card # or GHIN #</label>
              <input
                type="text"
                className="form-control"
                value={fd.memberNumber || ""}
                onChange={(e) => update("memberNumber", e.target.value.replace(/\D/g, ""))}
                placeholder="VIP or GHIN number"
                required
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button type="submit" className="btn btn-primary w-100" disabled={searchLoading}>
                {searchLoading ? "Searching…" : "Search"}
              </button>
            </div>
          </form>

          {hasSearched && searchResults.length > 0 && (
            <>
              {searchResults.every((r) => r.alreadyPurchased) ? (
                <div className="text-danger fw-semibold small mb-4">
                  Our records indicate you may already have a VIP Card. Please call the VSGA at (804) 378-2300 with any questions.
                </div>
              ) : (
                <div className="table-responsive mb-4">
                  <table className="table table-sm table-hover align-middle" style={{ fontSize: "0.85rem" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f1f3f5" }}>
                        <th>First Name</th><th>Last Name</th><th>Email</th><th>Address</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((r, i) => (
                        <tr key={i}>
                          <td>{r.firstName}</td>
                          <td>{r.lastName}</td>
                          <td>{r.email || "—"}</td>
                          <td>{r.address || "—"}</td>
                          <td>
                            {(r as any).isActiveCurrentYear && (
                              <span className="badge rounded-pill" style={{ backgroundColor: "#198754", fontSize: "0.72rem", fontWeight: 600 }}>
                                Active {(r as any).membershipYear || new Date().getFullYear()}
                              </span>
                            )}
                            {(r as any).purchaseSeason && (r as any).purchaseSeason !== (r as any).membershipYear && (
                              <span className="badge rounded-pill ms-1" style={{ backgroundColor: "#8c6e2a", fontSize: "0.72rem", fontWeight: 600 }}>
                                Renewing for {(r as any).purchaseSeason}
                              </span>
                            )}
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleSelectMember(r)}
                            >
                              Select →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {hasSearched && !searchLoading && searchResults.length === 0 && (
            <div className="alert alert-warning small">No results found.</div>
          )}

          <div className="text-center mb-4">
            <p className="small text-muted mb-2">New member? Create a fresh account.</p>
            <button type="button" className="btn btn-outline-secondary btn-sm px-4" onClick={handleNewMember}>
              Create New Account
            </button>
          </div>

          <div className="d-flex justify-content-between mt-3">
            <button type="button" className="btn btn-link text-decoration-none px-0" onClick={() => setStep(1)}>
              ← Back
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: Personal Info ── */}
      {step === 3 && (
        <div>
          <SectionHeader>Personal Information</SectionHeader>
          {fd.ghinNumber && (
            <div className="alert alert-light d-flex align-items-center small mb-4">
              <span className="me-2">ℹ</span>
              Updates here will also update the member's GHIN profile.
            </div>
          )}
          <form
            className="row gy-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (emailStatus === "error" || dobError) return;
              setStep(4);
            }}
          >
            <div className="col-md-6">
              <label className="form-label">First Name</label>
              <input type="text" className="form-control" value={fd.firstName || ""}
                onChange={(e) => update("firstName", e.target.value)} placeholder="First name" required />
            </div>
            <div className="col-md-6">
              <label className="form-label">Last Name</label>
              <input type="text" className="form-control" value={fd.lastName || ""}
                onChange={(e) => update("lastName", e.target.value)} placeholder="Last name" required />
            </div>
            <div className="col-md-6">
              <label className="form-label">Email <span className="text-muted fw-normal" style={{ fontSize: "0.8rem" }}>(optional)</span></label>
              <input type="email" className="form-control" value={fd.email || ""}
                onChange={(e) => update("email", e.target.value)}
                onBlur={handleEmailBlur} placeholder="email@example.com" />
              {emailStatus !== "idle" && (
                <small className={`form-text ${emailStatus === "error" ? "text-danger" : "text-muted"}`}>
                  {emailMessage}
                </small>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label">Phone</label>
              <input type="tel" className="form-control" value={fd.phone || ""}
                onChange={(e) => update("phone", e.target.value)}
                onKeyUp={(e) => update("phone", normalizeInput(e.currentTarget.value))}
                placeholder="(555) 555-5555" pattern="[0-9\(\)\-\s]{14,14}" minLength={14} maxLength={14} required />
            </div>
            <div className="col-md-6">
              <label className="form-label">Gender</label>
              <select className="form-select" value={fd.sex || ""}
                onChange={(e) => update("sex", e.target.value)} required>
                <option value="">Select gender…</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Date of Birth <span className="text-muted fw-normal" style={{ fontSize: "0.8rem" }}>(optional)</span></label>
              <input type="text" className={`form-control${dobError ? " is-invalid" : ""}`}
                value={fd.date_of_birth || ""}
                onChange={(e) => update("date_of_birth", e.target.value)}
                onKeyUp={handleDOBChange}
                placeholder="MM/DD/YYYY" />
              {dobError && <div className="text-danger small mt-1">{dobError}</div>}
            </div>
            <div className="col-12 d-flex justify-content-between mt-4">
              <button type="button" className="btn btn-link text-decoration-none px-0" onClick={() => setStep(2)}>← Back</button>
              <button type="submit" className="btn btn-primary px-4"
                disabled={emailStatus === "error" || emailStatus === "checking" || !!dobError}>
                Next →
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── STEP 4: Address ── */}
      {step === 4 && (
        <div>
          <SectionHeader>Address Information</SectionHeader>
          {fd.ghinNumber && (
            <div className="alert alert-light d-flex align-items-center small mb-4">
              <span className="me-2">ℹ</span>
              Updates here will also update the member's GHIN profile.
            </div>
          )}
          <form className="row gy-3" onSubmit={(e) => { e.preventDefault(); setStep(5); }}>
            <div className="col-12">
              <label className="form-label">Street Address</label>
              <input type="text" className="form-control" value={fd.address || ""}
                onChange={(e) => update("address", e.target.value)} placeholder="123 Main St" required />
            </div>
            <div className="col-12">
              <label className="form-label">Address Line 2</label>
              <input type="text" className="form-control" value={fd.street_address_2 || ""}
                onChange={(e) => update("street_address_2", e.target.value)} placeholder="Suite, apt, etc. (optional)" />
            </div>
            <div className="col-md-5">
              <label className="form-label">City</label>
              <input type="text" className="form-control" value={fd.city || ""}
                onChange={(e) => update("city", e.target.value)} placeholder="Richmond" required />
            </div>
            <div className="col-md-4">
              <label className="form-label">State</label>
              <select className="form-select" value={fd.state || "VA"}
                onChange={(e) => update("state", e.target.value)} required>
                <option value="">Select state…</option>
                {usaStates.map((s, i) => <option key={i} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">ZIP</label>
              <input type="text" className="form-control" value={fd.zip || ""}
                onChange={(e) => update("zip", e.target.value.replace(/\D/g, ""))}
                onKeyUp={(e) => update("zip", e.currentTarget.value.replace(/\D/g, ""))}
                placeholder="ZIP" maxLength={5} inputMode="numeric" pattern="[0-9]*" required />
            </div>
            <div className="col-12 d-flex justify-content-between mt-4">
              <button type="button" className="btn btn-link text-decoration-none px-0" onClick={() => setStep(3)}>← Back</button>
              <button type="submit" className="btn btn-primary px-4">Next →</button>
            </div>
          </form>
        </div>
      )}

      {/* ── STEP 5: Add-ons ── */}
      {step === 5 && (
        <div>
          <SectionHeader>Additional Options</SectionHeader>
          <p className="small text-muted mb-4">Bundle and save — add extras before completing your order.</p>

          {addOnsLoading && (
            <div className="text-center my-4">
              <div className="spinner-border spinner-border-sm me-2" role="status" />Loading add-ons…
            </div>
          )}

          <form className="row gy-4" onSubmit={(e) => { e.preventDefault(); killExistingAdminVP(); setStep(6); }}>
            {/* Bag Tag */}
            {addOnProducts.map((p) => (
              <div key={p.id} className="col-12 col-md-6">
                <div className="border rounded p-4">
                  <h6 className="fw-semibold mb-3" style={{ color: BRAND_GREEN }}>VSGA BAG TAG</h6>
                  <div className="form-check mb-3">
                    <input type="checkbox" id="bagTag" className="form-check-input"
                      checked={!!fd.bagTag}
                      onChange={(e) => {
                        update("bagTag", e.target.checked);
                        if (!e.target.checked) update("personalization", "");
                      }} />
                    <label className="form-check-label small" htmlFor="bagTag">
                      Add a personalized wooden bag tag for ${p.price?.toFixed(2)} (allow 6–8 weeks for delivery)
                    </label>
                  </div>
                  {fd.bagTag && (
                    <div>
                      <label className="form-label small">Personalization</label>
                      <input type="text" className="form-control form-control-sm"
                        value={fd.personalization || ""}
                        onChange={(e) => update("personalization", e.target.value)}
                        placeholder="Enter personalization text" required />
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Donation */}
            <div className="col-12 col-md-6">
              <div className="border rounded p-4">
                <h6 className="fw-semibold mb-3" style={{ color: BRAND_GREEN }}>SCHOLARSHIP DONATION</h6>
                <div className="form-check mb-3">
                  <input type="checkbox" id="donation" className="form-check-input"
                    checked={!!fd.donation}
                    onChange={(e) => {
                      update("donation", e.target.checked);
                      if (!e.target.checked) update("donation_amount", 0);
                    }} />
                  <label className="form-check-label small" htmlFor="donation">
                    Make a donation to the VSGA-VIP Scholarship Foundation
                  </label>
                </div>
                {fd.donation && (
                  <div>
                    <label className="form-label small">Donation Amount ($)</label>
                    <input type="number" className="form-control form-control-sm"
                      min="1" step="1"
                      value={fd.donation_amount && (fd.donation_amount as number) > 0 ? fd.donation_amount : ""}
                      onChange={(e) => update("donation_amount", e.target.value === "" ? 0 : Number(e.target.value))}
                      placeholder="Enter amount" required />
                  </div>
                )}
              </div>
            </div>

            <div className="col-12 d-flex justify-content-between mt-4">
              <button type="button" className="btn btn-link text-decoration-none px-0" onClick={() => setStep(4)}>← Back</button>
              <button type="submit" className="btn btn-primary px-4">Next →</button>
            </div>
          </form>
        </div>
      )}

      {/* ── STEP 6: Payment ── */}
      {step === 6 && (
        <div>
          {(vpPageLoading || vpLoading) && paymentMethod === "credit_card" && (
            <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{ background: "rgba(255,255,255,0.85)", zIndex: 1040 }}>
              <div className="text-center">
                <div className="spinner-border mb-3" style={{ color: BRAND_GREEN }} />
                <p className="fw-semibold">
                  {vpLoading ? "Processing payment…" : "Loading secure checkout…"}
                </p>
              </div>
            </div>
          )}

          <SectionHeader>Payment</SectionHeader>

          <div className="row g-4">
            {/* Left: form */}
            <div className="col-lg-7">
              <h6 className="text-uppercase mb-3">VIP Cardholder Information</h6>
              <div className="row gy-3 mb-4">
                <div className="col-md-6">
                  <label className="form-label small">First Name</label>
                  <input type="text" className="form-control" value={fd.firstName || ""}
                    onChange={(e) => update("firstName", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Last Name</label>
                  <input type="text" className="form-control" value={fd.lastName || ""}
                    onChange={(e) => update("lastName", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Email</label>
                  <input type="email" className="form-control" value={fd.email || ""}
                    onChange={(e) => update("email", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Phone</label>
                  <input type="tel" className="form-control" value={fd.phone || ""}
                    onChange={(e) => update("phone", e.target.value)} />
                </div>
              </div>

              {/* Billing address toggle */}
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="sameAsBilling"
                  checked={sameAsBilling}
                  onChange={(e) => setSameAsBilling(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="sameAsBilling">
                  Billing address same as member address
                </label>
              </div>

              {!sameAsBilling && (
                <div className="row gy-3 mb-4">
                  <div className="col-12">
                    <label className="form-label small">Billing Street Address</label>
                    <input type="text" className="form-control" placeholder="123 Main St"
                      value={fd.ShippingAddress || ""}
                      onChange={(e) => update("ShippingAddress", e.target.value)} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small">Address Line 2</label>
                    <input type="text" className="form-control" placeholder="Suite, apt, etc. (optional)"
                      value={fd.ShippingStreet_address_2 || ""}
                      onChange={(e) => update("ShippingStreet_address_2", e.target.value)} />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label small">City</label>
                    <input type="text" className="form-control" placeholder="City"
                      value={fd.ShippingCity || ""}
                      onChange={(e) => update("ShippingCity", e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">State</label>
                    <select className="form-select" value={fd.ShippingState || "VA"}
                      onChange={(e) => update("ShippingState", e.target.value)}>
                      <option value="">Select state…</option>
                      {usaStates.map((s, i) => <option key={i} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">ZIP</label>
                    <input type="text" className="form-control" placeholder="ZIP" maxLength={5}
                      inputMode="numeric" pattern="[0-9]*"
                      value={fd.ShippingZip || ""}
                      onChange={(e) => update("ShippingZip", e.target.value.replace(/\D/g, ""))} />
                  </div>
                </div>
              )}

              <h6 className="text-uppercase mb-3">Payment Method</h6>

              <div className="mb-4">
                {(["credit_card", "check", "cash", "misc"] as const).map((method) => (
                  <div className="form-check" key={method}>
                    <input
                      type="radio"
                      className="form-check-input"
                      id={`pm-admin-${method}`}
                      name="adminPaymentMethod"
                      value={method}
                      checked={paymentMethod === method}
                      onChange={() => {
                        setPaymentMethod(method);
                        setCheckNumber("");
                        setMiscNote("");
                        setVpError(null);
                        setPayError(null);
                        if (method === "credit_card") {
                          setVpRetryNonce((n) => n + 1);
                        } else {
                          clearVPSession();
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor={`pm-admin-${method}`}>
                      {method === "credit_card" ? "Credit Card" : method === "check" ? "Check" : method === "cash" ? "Cash" : "Miscellaneous"}
                    </label>
                  </div>
                ))}
              </div>

              {paymentMethod === "credit_card" && (
                <div ref={containerRef} id="versapay-checkout-container-admin"
                  style={{ minHeight: "50px", width: "100%" }} />
              )}

              {paymentMethod === "check" && (
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Check Number</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter check number"
                    value={checkNumber}
                    onChange={(e) => setCheckNumber(e.target.value)}
                  />
                </div>
              )}

              {paymentMethod === "misc" && (
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Note</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter note"
                    value={miscNote}
                    onChange={(e) => setMiscNote(e.target.value)}
                  />
                </div>
              )}

              {(vpError || payError) && (
                <div className="alert alert-danger mt-3 small" role="alert">
                  {vpError || payError}
                  {vpError && !processingPayment && paymentMethod === "credit_card" && (
                    <div className="mt-2">
                      <button type="button" className="btn btn-sm btn-outline-danger"
                        onClick={() => { if (safetyTimeoutRef.current) { clearTimeout(safetyTimeoutRef.current); safetyTimeoutRef.current = null; } setVpError(null); setVpRetryNonce((n) => n + 1); }}>
                        Try Again
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="small text-muted mt-3">
                <p className="mb-2">
                  NOTE: Only one card per person. VIP cards are the property of the VSGA and WVGA.
                  Cards are non-transferable and non-refundable.
                </p>
              </div>

              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-primary w-100 py-2"
                  disabled={
                    processingPayment ||
                    (paymentMethod === "credit_card" && (vpLoading || (finalTotal > 0 && vpPageLoading) || !!vpError)) ||
                    (paymentMethod === "check" && !checkNumber.trim())
                  }
                  onClick={
                    paymentMethod === "credit_card"
                      ? (finalTotal === 0 ? handleFreeOrder : handlePay)
                      : handleOfflinePayment
                  }
                >
                  {processingPayment ? "Processing…" : (finalTotal === 0 && paymentMethod === "credit_card") ? "Complete Order" : "Submit Payment"}
                </button>
              </div>

              {processingPayment && paymentMethod === "credit_card" && (
                <div className="mt-2 text-center">
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger text-decoration-none"
                    onClick={handleCancelPayment}
                  >
                    Cancel &amp; Try Again
                  </button>
                </div>
              )}

              <div className="mt-3">
                <button type="button" className="btn btn-link text-decoration-none px-0"
                  disabled={processingPayment}
                  onClick={() => { clearVPSession(); setStep(5); }}>
                  ← Back
                </button>
              </div>
            </div>

            {/* Right: order summary */}
            <div className="col-lg-5">
              <div className="border rounded p-4 bg-white">
                <h6 className="text-uppercase mb-3">Order Summary</h6>

                {vipProduct && (
                  <div className="d-flex justify-content-between small mb-2">
                    <span>
                      <strong className="text-uppercase">{fd.vip_package}</strong> package
                      <br />
                      <span className="text-muted">{fd.vip_card_type}</span>
                    </span>
                    <span>${unitPrice.toFixed(2)}</span>
                  </div>
                )}

                {fd.bagTag && (() => {
                  const TAX_RATE = 0.06;
                  const bagTagPreTax = Math.round((bagTagTotal / (1 + TAX_RATE)) * 100) / 100;
                  const bagTagTax   = Math.round((bagTagTotal - bagTagPreTax) * 100) / 100;
                  return (
                    <>
                      <div className="d-flex justify-content-between small mb-1">
                        <span>Bag Tag <span className="text-muted">({fd.personalization})</span> — Subtotal</span>
                        <span>${bagTagPreTax.toFixed(2)}</span>
                      </div>
                      <div className="d-flex justify-content-between small mb-1 text-muted">
                        <span style={{ paddingLeft: "0.75rem" }}>Tax (6%)</span>
                        <span>${bagTagTax.toFixed(2)}</span>
                      </div>
                      <div className="d-flex justify-content-between small mb-2">
                        <span style={{ paddingLeft: "0.75rem" }}>Bag Tag Total</span>
                        <span>${bagTagTotal.toFixed(2)}</span>
                      </div>
                    </>
                  );
                })()}

                {fd.donation && Number(fd.donation_amount) > 0 && (
                  <div className="d-flex justify-content-between small mb-2">
                    <span>Donation</span>
                    <span>${donationTotal.toFixed(2)}</span>
                  </div>
                )}

                <hr className="my-2" />

                {promoApplied && discount > 0 && (
                  <div className="d-flex justify-content-between small mb-2 text-success">
                    <span>Promo: {appliedPromoCode}</span>
                    <span>-${discount.toFixed(2)}</span>
                  </div>
                )}

                <div className="d-flex justify-content-between fw-bold small mb-3">
                  <span>Total</span>
                  <span>${finalTotal.toFixed(2)}</span>
                </div>

                {/* Promo code */}
                <div className="mt-2">
                  <label className="form-label small">Promo / Gift Code</label>
                  {promoApplied ? (
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge" style={{ backgroundColor: BRAND_GREEN }}>{appliedPromoCode}</span>
                      <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={removePromo}>Remove</button>
                    </div>
                  ) : (
                    <div className="input-group input-group-sm">
                      <input type="text" className="form-control" placeholder="Enter code"
                        value={promoInput}
                        onChange={(e) => { setPromoError(null); setPromoInput(e.target.value); }} />
                      <button type="button" className="btn btn-outline-secondary"
                        disabled={isApplyingPromo}
                        onClick={applyPromoCode}>
                        {isApplyingPromo ? "…" : "Apply"}
                      </button>
                    </div>
                  )}
                  {promoError && <div className="text-danger small mt-1">{promoError}</div>}
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 7: Confirmation ── */}
      {step === 7 && completedGolfer && (
        <div>
          <div className="text-center py-4">
            <div style={{ fontSize: "3rem", color: BRAND_GREEN }}>✓</div>
            <h5 className="mt-2 fw-semibold" style={{ color: BRAND_GREEN }}>Order Complete</h5>
            <p className="text-muted small">
              The membership has been successfully purchased and recorded in HubSpot.
            </p>
          </div>

          <div className="border rounded p-4 mb-4" style={{ maxWidth: 480, margin: "0 auto" }}>
            <h6 className="text-uppercase small fw-semibold mb-3" style={{ color: BRAND_GREEN }}>
              Order Details
            </h6>
            <div className="row gy-2 small">
              <div className="col-5 text-muted">Member</div>
              <div className="col-7 fw-semibold">{completedGolfer.firstName} {completedGolfer.lastName}</div>
              <div className="col-5 text-muted">Email</div>
              <div className="col-7">{completedGolfer.email}</div>
              <div className="col-5 text-muted">Package</div>
              <div className="col-7 text-uppercase">{completedGolfer.vip_package}</div>
              <div className="col-5 text-muted">Format</div>
              <div className="col-7">{completedGolfer.vip_card_type}</div>
              {assignedVipCardNumber && (
                <>
                  <div className="col-5 text-muted">VIP Card #</div>
                  <div className="col-7" style={{ fontFamily: "monospace", fontWeight: 600 }}>
                    {assignedVipCardNumber}
                    {!completedGolfer.memberNumber && (
                      <span className="ms-2 badge bg-success" style={{ fontSize: "0.7rem", fontFamily: "sans-serif" }}>
                        Generated
                      </span>
                    )}
                  </div>
                </>
              )}
              {(completedGolfer as any).versapayTransactionId && (
                <>
                  <div className="col-5 text-muted">Transaction #</div>
                  <div className="col-7" style={{ fontFamily: "monospace" }}>
                    {(completedGolfer as any).versapayTransactionId}
                  </div>
                </>
              )}
              {fd.ownerId && owners.length > 0 && (
                <>
                  <div className="col-5 text-muted">Assigned to</div>
                  <div className="col-7">
                    {owners.find((o) => o.id === fd.ownerId)?.firstName}{" "}
                    {owners.find((o) => o.id === fd.ownerId)?.lastName}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="text-center">
            <button type="button" className="btn btn-primary px-5" onClick={handleStartOver}>
              Purchase Another Membership
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseMembership;
